import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  MapPin, Calendar, Clock, Users, ChevronDown, ChevronUp,
  Download, Trash2, Plus, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FourthOfficial = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedLineup, setExpandedLineup] = useState({ 1: true, 2: true });
  
  // Event form state
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventType, setEventType] = useState('goal');
  const [eventTeam, setEventTeam] = useState(1);
  const [eventMinute, setEventMinute] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [playerNumber, setPlayerNumber] = useState('');
  const [secondPlayerName, setSecondPlayerName] = useState('');
  const [secondPlayerNumber, setSecondPlayerNumber] = useState('');
  const [eventNotes, setEventNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid link - no token provided');
      setLoading(false);
      return;
    }
    fetchData();
    // Poll for updates every 5 seconds
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/api/fourth-official/${token}`);
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load match data');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPeriodDisplay = (period) => {
    const periodMap = {
      'pregame': 'Pre-Game',
      '1st_half': '1st Half',
      'halftime': 'Half Time',
      '2nd_half': '2nd Half',
      'fulltime': 'Full Time',
      '1st_half_et': '1st Half ET',
      'halftime_et': 'Half Time ET',
      '2nd_half_et': '2nd Half ET',
      'penalties': 'Penalties'
    };
    return periodMap[period] || period;
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!playerName || !eventMinute) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/fourth-official/${token}/event`, {
        event_type: eventType,
        team: eventTeam,
        minute: parseInt(eventMinute),
        player_name: playerName,
        player_number: playerNumber,
        second_player_name: secondPlayerName,
        second_player_number: secondPlayerNumber,
        notes: eventNotes
      });
      
      // Reset form
      setPlayerName('');
      setPlayerNumber('');
      setSecondPlayerName('');
      setSecondPlayerNumber('');
      setEventMinute('');
      setEventNotes('');
      setShowEventForm(false);
      
      // Refresh data
      fetchData();
    } catch (err) {
      alert('Failed to add event: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    
    try {
      await axios.delete(`${API}/api/fourth-official/${token}/event/${eventId}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete event');
    }
  };

  const handleDownloadReport = () => {
    window.open(`${API}/api/fourth-official/${token}/report`, '_blank');
  };

  const toggleLineup = (teamNum) => {
    setExpandedLineup(prev => ({
      ...prev,
      [teamNum]: !prev[teamNum]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white font-body">Loading match data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4">
        <Card className="bg-[#1E293B] border-red-500/50 max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-400 font-body text-lg mb-2">Access Denied</p>
            <p className="text-slate-400 font-body">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sport = data.sport || 'soccer';
  const isGaelic = sport === 'gaelic_football' || sport === 'hurling';

  return (
    <div className="min-h-screen bg-[#0F172A] p-4 pb-24">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="text-center mb-4">
          <p className="text-[#F97316] font-body text-xs uppercase tracking-widest mb-1">
            4th Official Panel
          </p>
          <h1 className="text-white font-heading text-2xl md:text-3xl">
            {data.team1.name} vs {data.team2.name}
          </h1>
          {data.competition && (
            <p className="text-slate-400 font-body mt-1">{data.competition}</p>
          )}
        </div>
        
        {/* Match Info */}
        <div className="flex flex-wrap justify-center gap-4 text-slate-400 font-body text-sm mb-4">
          {data.venue && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {data.venue}
            </div>
          )}
          {data.match_date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {data.match_date}
            </div>
          )}
          {data.match_time && (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {data.match_time}
            </div>
          )}
        </div>

        {/* Live Score Display */}
        <Card className="bg-[#1E293B] border-[#234968]">
          <CardContent className="py-4">
            <div className="flex items-center justify-center gap-4">
              {/* Team 1 */}
              <div className="text-center flex-1">
                <p className="text-white font-heading text-lg truncate">{data.team1.name}</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  {data.team1.yellow_cards > 0 && (
                    <span className="bg-yellow-500 text-black text-xs px-1.5 rounded">{data.team1.yellow_cards}</span>
                  )}
                  {data.team1.red_cards > 0 && (
                    <span className="bg-red-600 text-white text-xs px-1.5 rounded">{data.team1.red_cards}</span>
                  )}
                </div>
              </div>
              
              {/* Score */}
              <div className="text-center px-6 py-2 bg-[#234968] rounded-lg">
                <p className="text-white font-heading text-3xl">
                  {isGaelic 
                    ? `${data.team1.goals}-${data.team1.points}` 
                    : data.team1.score
                  }
                  <span className="mx-2 text-slate-400">-</span>
                  {isGaelic 
                    ? `${data.team2.goals}-${data.team2.points}` 
                    : data.team2.score
                  }
                </p>
                <p className="text-slate-300 text-sm mt-1">
                  {formatTime(data.current_time)} • {getPeriodDisplay(data.period)}
                </p>
              </div>
              
              {/* Team 2 */}
              <div className="text-center flex-1">
                <p className="text-white font-heading text-lg truncate">{data.team2.name}</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  {data.team2.yellow_cards > 0 && (
                    <span className="bg-yellow-500 text-black text-xs px-1.5 rounded">{data.team2.yellow_cards}</span>
                  )}
                  {data.team2.red_cards > 0 && (
                    <span className="bg-red-600 text-white text-xs px-1.5 rounded">{data.team2.red_cards}</span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Events Section */}
      <div className="max-w-4xl mx-auto mb-6">
        <Card className="bg-[#1E293B] border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white font-heading text-lg flex items-center gap-2">
              Match Events
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadReport}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="download-report-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                PDF Report
              </Button>
              <Button
                size="sm"
                onClick={() => setShowEventForm(!showEventForm)}
                className="bg-[#F97316] hover:bg-[#EA580C]"
                data-testid="add-event-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Event
              </Button>
            </div>
          </CardHeader>
          
          <CardContent>
            {/* Add Event Form */}
            {showEventForm && (
              <form onSubmit={handleAddEvent} className="bg-[#0F172A] rounded-lg p-4 mb-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Event Type */}
                  <div>
                    <Label className="text-slate-300 text-sm">Event Type</Label>
                    <select
                      value={eventType}
                      onChange={(e) => setEventType(e.target.value)}
                      className="w-full mt-1 bg-slate-800 border-slate-600 rounded-md px-3 py-2 text-white"
                      data-testid="event-type-select"
                    >
                      <option value="goal">⚽ Goal</option>
                      <option value="yellow_card">🟨 Yellow Card</option>
                      <option value="red_card">🟥 Red Card</option>
                      <option value="substitution">🔄 Substitution</option>
                    </select>
                  </div>
                  
                  {/* Team */}
                  <div>
                    <Label className="text-slate-300 text-sm">Team</Label>
                    <select
                      value={eventTeam}
                      onChange={(e) => setEventTeam(parseInt(e.target.value))}
                      className="w-full mt-1 bg-slate-800 border-slate-600 rounded-md px-3 py-2 text-white"
                      data-testid="event-team-select"
                    >
                      <option value={1}>{data.team1.name}</option>
                      <option value={2}>{data.team2.name}</option>
                    </select>
                  </div>
                  
                  {/* Minute */}
                  <div>
                    <Label className="text-slate-300 text-sm">Minute</Label>
                    <Input
                      type="number"
                      value={eventMinute}
                      onChange={(e) => setEventMinute(e.target.value)}
                      placeholder="45"
                      className="mt-1 bg-slate-800 border-slate-600 text-white"
                      required
                      data-testid="event-minute-input"
                    />
                  </div>
                  
                  {/* Player Number */}
                  <div>
                    <Label className="text-slate-300 text-sm">Player #</Label>
                    <Input
                      type="text"
                      value={playerNumber}
                      onChange={(e) => setPlayerNumber(e.target.value)}
                      placeholder="10"
                      className="mt-1 bg-slate-800 border-slate-600 text-white"
                      data-testid="player-number-input"
                    />
                  </div>
                </div>
                
                {/* Player Name */}
                <div>
                  <Label className="text-slate-300 text-sm">
                    {eventType === 'substitution' ? 'Player Out' : 'Player Name'}
                  </Label>
                  <Input
                    type="text"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    placeholder="Enter player name"
                    className="mt-1 bg-slate-800 border-slate-600 text-white"
                    required
                    data-testid="player-name-input"
                  />
                </div>
                
                {/* Second Player (for substitutions) */}
                {eventType === 'substitution' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm">Player In</Label>
                      <Input
                        type="text"
                        value={secondPlayerName}
                        onChange={(e) => setSecondPlayerName(e.target.value)}
                        placeholder="Enter player name"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                        data-testid="second-player-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Player In #</Label>
                      <Input
                        type="text"
                        value={secondPlayerNumber}
                        onChange={(e) => setSecondPlayerNumber(e.target.value)}
                        placeholder="7"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                        data-testid="second-player-number-input"
                      />
                    </div>
                  </div>
                )}
                
                {/* Notes */}
                <div>
                  <Label className="text-slate-300 text-sm">Notes (optional)</Label>
                  <Input
                    type="text"
                    value={eventNotes}
                    onChange={(e) => setEventNotes(e.target.value)}
                    placeholder="Any additional notes..."
                    className="mt-1 bg-slate-800 border-slate-600 text-white"
                    data-testid="event-notes-input"
                  />
                </div>
                
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowEventForm(false)}
                    className="border-slate-600 text-slate-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#234968] hover:bg-[#1a3650]"
                    data-testid="submit-event-btn"
                  >
                    {submitting ? 'Adding...' : 'Add Event'}
                  </Button>
                </div>
              </form>
            )}
            
            {/* Events List */}
            {data.match_events && data.match_events.length > 0 ? (
              <div className="space-y-2">
                {[...data.match_events]
                  .sort((a, b) => a.minute - b.minute)
                  .map((event) => (
                  <div 
                    key={event.id} 
                    className="flex items-center gap-3 bg-[#0F172A] rounded-lg p-3"
                    data-testid={`event-${event.id}`}
                  >
                    <span className="font-mono text-slate-400 w-10 text-center">{event.minute}'</span>
                    <span className="text-lg w-8">
                      {event.event_type === 'goal' && '⚽'}
                      {event.event_type === 'yellow_card' && '🟨'}
                      {event.event_type === 'red_card' && '🟥'}
                      {event.event_type === 'substitution' && '🔄'}
                    </span>
                    <div className="flex-1">
                      <p className="text-white font-body">
                        {event.player_number && `#${event.player_number} `}
                        {event.player_name}
                        {event.event_type === 'substitution' && event.second_player_name && (
                          <span className="text-slate-400"> ↔ {event.second_player_name}</span>
                        )}
                      </p>
                      <p className="text-slate-500 text-xs font-body">
                        {event.team === 1 ? data.team1.name : data.team2.name}
                        {event.notes && <span className="ml-2 text-slate-600">• {event.notes}</span>}
                      </p>
                    </div>
                    {event.source === 'fourth_official' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEvent(event.id)}
                        className="text-slate-500 hover:text-red-400 hover:bg-red-900/20"
                        data-testid={`delete-event-${event.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500 font-body">No events recorded yet</p>
                <p className="text-slate-600 font-body text-sm mt-1">
                  Click "Add Event" to log match events
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lineups Section */}
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-2 gap-4">
          {/* Team 1 Lineup */}
          <Card className="bg-[#1E293B] border-slate-700">
            <CardHeader 
              className="cursor-pointer hover:bg-slate-700/30"
              onClick={() => toggleLineup(1)}
            >
              <CardTitle className="text-white font-heading text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-slate-400" />
                  {data.team1.name}
                </span>
                {expandedLineup[1] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
              {data.team1.formation && (
                <p className="text-slate-400 text-sm">Formation: {data.team1.formation}</p>
              )}
              {data.team1.manager && (
                <p className="text-slate-400 text-sm">Manager: {data.team1.manager}</p>
              )}
            </CardHeader>
            
            {expandedLineup[1] && (
              <CardContent>
                {data.team1.lineup_submitted ? (
                  <>
                    {/* Starting XI */}
                    <div className="mb-4">
                      <p className="text-slate-400 font-body text-xs uppercase tracking-wider mb-2">
                        Starting XI
                      </p>
                      <div className="bg-[#0F172A] rounded-lg overflow-hidden">
                        {(data.team1.starting || []).map((player, idx) => (
                          <div key={player.id || `t1-st-${player.number}-${idx}`} className="flex items-center gap-3 p-2 border-b border-slate-800 last:border-0">
                            <span className="w-8 h-8 rounded-full bg-[#234968] flex items-center justify-center text-white text-sm font-bold">
                              {player.number}
                            </span>
                            <div className="flex-1">
                              <p className="text-white text-sm">{player.name}</p>
                              <p className="text-slate-500 text-xs">{player.position}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Substitutes */}
                    {data.team1.substitutes && data.team1.substitutes.length > 0 && (
                      <div>
                        <p className="text-slate-400 font-body text-xs uppercase tracking-wider mb-2">
                          Substitutes
                        </p>
                        <div className="bg-[#0F172A] rounded-lg overflow-hidden">
                          {data.team1.substitutes.map((player, idx) => (
                            <div key={player.id || `t1-sub-${player.number}-${idx}`} className="flex items-center gap-3 p-2 border-b border-slate-800 last:border-0">
                              <span className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-slate-300 text-sm font-bold">
                                {player.number}
                              </span>
                              <div className="flex-1">
                                <p className="text-slate-300 text-sm">{player.name}</p>
                                <p className="text-slate-500 text-xs">{player.position}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-slate-500 font-body">Lineup not yet submitted</p>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
          
          {/* Team 2 Lineup */}
          <Card className="bg-[#1E293B] border-slate-700">
            <CardHeader 
              className="cursor-pointer hover:bg-slate-700/30"
              onClick={() => toggleLineup(2)}
            >
              <CardTitle className="text-white font-heading text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-slate-400" />
                  {data.team2.name}
                </span>
                {expandedLineup[2] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
              {data.team2.formation && (
                <p className="text-slate-400 text-sm">Formation: {data.team2.formation}</p>
              )}
              {data.team2.manager && (
                <p className="text-slate-400 text-sm">Manager: {data.team2.manager}</p>
              )}
            </CardHeader>
            
            {expandedLineup[2] && (
              <CardContent>
                {data.team2.lineup_submitted ? (
                  <>
                    {/* Starting XI */}
                    <div className="mb-4">
                      <p className="text-slate-400 font-body text-xs uppercase tracking-wider mb-2">
                        Starting XI
                      </p>
                      <div className="bg-[#0F172A] rounded-lg overflow-hidden">
                        {(data.team2.starting || []).map((player, idx) => (
                          <div key={player.id || `t2-st-${player.number}-${idx}`} className="flex items-center gap-3 p-2 border-b border-slate-800 last:border-0">
                            <span className="w-8 h-8 rounded-full bg-[#234968] flex items-center justify-center text-white text-sm font-bold">
                              {player.number}
                            </span>
                            <div className="flex-1">
                              <p className="text-white text-sm">{player.name}</p>
                              <p className="text-slate-500 text-xs">{player.position}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Substitutes */}
                    {data.team2.substitutes && data.team2.substitutes.length > 0 && (
                      <div>
                        <p className="text-slate-400 font-body text-xs uppercase tracking-wider mb-2">
                          Substitutes
                        </p>
                        <div className="bg-[#0F172A] rounded-lg overflow-hidden">
                          {data.team2.substitutes.map((player, idx) => (
                            <div key={player.id || `t2-sub-${player.number}-${idx}`} className="flex items-center gap-3 p-2 border-b border-slate-800 last:border-0">
                              <span className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-slate-300 text-sm font-bold">
                                {player.number}
                              </span>
                              <div className="flex-1">
                                <p className="text-slate-300 text-sm">{player.name}</p>
                                <p className="text-slate-500 text-xs">{player.position}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-slate-500 font-body">Lineup not yet submitted</p>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-8 text-slate-600 font-body text-xs">
        Auto-refreshes every 5 seconds • 4th Official Panel
      </div>
    </div>
  );
};

export default FourthOfficial;
